import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { ChevronDownIcon, SearchIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { SubSideNavItemLabel } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemLabel';
import { SubSideNavItemPrefix } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemPrefix';
import { workspaceSidebarNavigationItem } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItem';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { Pathnames } from '~/components/v2/constants/pathnames';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';

export const SearchSubSideNavSearchAllItem = memo(() => {
  const { pathname } = useRouter();
  const { trackSidebarInteraction } = useTrackSidebarInteraction();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const isSelected = pathname === Pathnames.Search || pathname.startsWith(`${Pathnames.Search}/`);

  const handleOpenSearch = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'search' });
  }, [trackSidebarInteraction]);

  const searchFiltersToggleLabel = collapsed ? 'Show search filters' : 'Hide search filters';

  return (
    <div className="px-2.5 py-2">
      <div
        className={workspaceSidebarNavigationItem({ appearance: 'subNav', className: 'w-full' })}
        data-selected={isSelected}
      >
        <Link
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-8 text-inherit no-underline outline-none hover:no-underline focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
          data-item-id="all-assets"
          data-selected={isSelected}
          data-testid="SUB_SIDE_NAV_ITEM"
          href={{ pathname: Pathnames.Search, query: getCanvasQueryParams() }}
          onClick={handleOpenSearch}
        >
          <SubSideNavItemPrefix className="text-gray-10 group-data-[selected=true]/workspace-sidebar-navigation-item:text-gray-12">
            <SearchIcon color="currentColor" size="sm" />
          </SubSideNavItemPrefix>
          <SubSideNavItemLabel>Search all</SubSideNavItemLabel>
        </Link>
        <Tooltip label={searchFiltersToggleLabel}>
          <IconButton
            appearance="ghost"
            aria-expanded={!collapsed}
            className="bg-transparent hover:bg-transparent"
            color="gray"
            data-testid="SEARCH_SUB_SIDE_NAV_TOGGLE"
            label={searchFiltersToggleLabel}
            onClick={toggleCollapsed}
            size="small"
          >
            <ChevronDownIcon className={cn('transition-transform', collapsed && '-rotate-90')} size="sm" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

SearchSubSideNavSearchAllItem.displayName = 'SearchSubSideNavSearchAllItem';
